import React, {useRef, useEffect, useState} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    CalendarDays, Wallet, Coins, AlertCircle, UserPlus, MailCheck, FileText, Clock, CreditCard,
    Plus, ArrowRight, RefreshCw,
} from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { useQuery } from "@/lib/queryCache";
import { SkeletonBlock, SkeletonLine } from "@/components/Skeletons";

const STATUS_STYLE = {
    paid:      "bg-emerald-500/20 border-emerald-400/40 text-emerald-100",
    pending:   "bg-amber-500/20 border-amber-400/40 text-amber-100",
    canceled:  "bg-red-500/20 border-red-400/40 text-red-100",
    refunded:  "bg-blue-500/20 border-blue-400/40 text-blue-100",
};

export function StaffDashboard() {
    const { user } = useAuth();
    const nav = useNavigate();

    const forceRefresh = useRef(false);
    const { data, error, isLoading, isFetching, hasStaleData, refetch } = useQuery(
        "staff:dashboard",
        () => { const fresh=forceRefresh.current; forceRefresh.current=false; return staffApi.dashboard(fresh); },
        { staleMs: 30_000 },
    );

    const refreshDashboard = () => { forceRefresh.current=true; return refetch(); };
    useEffect(()=>{
        if(!data?.paymentsRefreshing)return;
        const timer=setInterval(()=>refetch(),3000);
        return()=>clearInterval(timer);
    },[data?.paymentsRefreshing,refetch]);
    const greeting = greetForNow();
    const [activityNow, setActivityNow] = useState(Date.now);
    useEffect(() => {
        const tick = () => setActivityNow(Date.now());
        const timer = setInterval(tick, 60000);
        window.addEventListener('focus', tick);
        return () => {clearInterval(timer); window.removeEventListener('focus', tick);};
    }, []);
    const recentActivity = (data?.recentPayments || []).filter(p => {
        const at = Date.parse(p.activityAt);
        return Number.isFinite(at) && at <= activityNow && at >= activityNow - 86400000;
    }).sort((a,b) => Date.parse(b.activityAt)-Date.parse(a.activityAt));

    return (
        <div className="space-y-4 md:space-y-6" data-testid="staff-dashboard">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-gold font-semibold">{greeting}</div>
                    <h1 className="font-serif font-medium text-3xl md:text-5xl text-navy leading-[1.05] mt-1">
                        {greeting}, {user?.name || "Angela"}.
                    </h1>
                    <p className="text-sm md:text-base text-navy/60 mt-2">
                        Here's what needs your attention today{data?.today ? ` · ${formatUKDate(data.today)}` : ""}
                    </p>
                </div>
                <div className="flex gap-2 items-center">
                    {hasStaleData && (
                        <span className="text-[10px] uppercase tracking-[0.18em] text-amber-700" data-testid="dash-stale-indicator">
                            {isFetching ? "Refreshing…" : "Showing cached data"}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={refreshDashboard}
                        aria-label="Refresh"
                        data-testid="dashboard-refresh"
                        className="h-11 w-11 rounded-2xl bg-white border hairline shadow-sm grid place-items-center hover:border-gold/60 transition-colors"
                    >
                        <RefreshCw className={`h-4 w-4 text-navy ${isFetching ? "animate-spin" : ""}`} strokeWidth={1.75} />
                    </button>
                    <Link to="/staff/booking/new" data-testid="quick-new-booking"
                          className="h-11 px-6 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center gap-2 shadow-[0_10px_20px_-10px_rgba(197,160,89,0.6)] hover:bg-gold-light transition-colors">
                        <Plus className="h-4 w-4" strokeWidth={2.5} /> New booking
                    </Link>
                </div>
            </header>
            {data?.paymentsRefreshing&&<p role="status" className="text-sm text-navy/70">Payment figures are updating in the background{data.paymentsVerifiedAt?` · Last verified ${new Date(data.paymentsVerifiedAt).toLocaleString('en-GB')}`:''}.</p>}

            {/* Show skeletons only on cold-cache first load. If we still have stale data, we render it under `data`. */}
            {isLoading && !data && <DashboardSkeleton />}

            {/* Connection issue banner — navy highlight card matching mockup */}
            {error && !data && (
                <div className="surface-highlight rounded-2xl p-5 md:p-6 flex items-start gap-4" data-testid="dashboard-error">
                    <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center shrink-0">
                        <AlertCircle className="h-5 w-5 text-gold" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold">Connection issue</div>
                        <h3 className="font-serif text-xl text-white mt-1">We couldn't reach the booking system.</h3>
                        <p className="text-sm text-white/70 mt-1">
                            {error.message || "The booking system is not responding right now. Please try again shortly."}
                        </p>
                        <button type="button" onClick={refreshDashboard}
                                className="mt-3 text-xs text-gold border border-gold/50 rounded-full px-4 py-1.5 font-semibold hover:bg-gold/10 transition-colors">
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {data && (
              <>
                {data.stale && (
                    <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-2.5 text-xs text-amber-100 flex items-center gap-2" data-testid="dash-stale-banner">
                        <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-amber-200">Cached</span>
                        <span>Showing the last saved snapshot — the latest dashboard could not be loaded.</span>
                        <button type="button" onClick={refreshDashboard} className="ml-auto text-amber-200 hover:text-white text-[11px] underline">Refresh</button>
                    </div>
                )}
                {data.paymentsAvailable === false && <div role="status" className="rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-navy">Appointment totals are up to date. Outstanding balances could not be verified. <button type="button" onClick={refreshDashboard} className="underline font-semibold">Try again</button></div>}
                {/* KPIs — one uniform 2-column (mobile) / 4-column (desktop) grid, all navy */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-kpis">
                    <KPI onClick={() => nav("/staff/calendar")} icon={CalendarDays}
                         label="Today's appointments" value={data.todaysAppointments.length}
                         sub={pluralise(data.todaysAppointments.length, "appointment", "appointments")}
                         testId="kpi-today-appts" />
                    <KPI icon={Wallet} label="Today's revenue" value={formatMoney(data.todaysRevenue)} sub="Booked value" testId="kpi-today-revenue" />
                    <KPI icon={Coins} label="Today's deposits" value={formatMoney(data.todaysDeposits)} sub="Deposits received" testId="kpi-today-deposits" />
                    <KPI onClick={() => nav("/staff/payments?status=outstanding")} icon={AlertCircle}
                         label="Outstanding payments" value={data.outstandingTotal == null ? "—" : formatMoney(data.outstandingTotal)}
                         sub={data.outstandingCount == null ? "Balances awaiting verification" : `${data.outstandingCount} appt${data.outstandingCount === 1 ? "" : "s"}`}
                         testId="kpi-outstanding" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI onClick={() => nav("/staff/customers")} icon={UserPlus} label="New customers today" value={data.newCustomersToday} testId="kpi-new-customers" />
                    <KPI onClick={() => nav("/staff/communications/queue")} icon={MailCheck} label="Messages to send" value={data.unreadComms} testId="kpi-unread-comms" />
                    <KPI onClick={() => nav("/staff/forms/review")} icon={FileText} label="Forms to review" value={data.pendingForms} testId="kpi-pending-forms" />
                    <KPI onClick={() => nav("/staff/payments?status=deposit-outstanding")} icon={Coins} label="Outstanding deposits" value={data.outstandingDepositTotal == null ? "—" : formatMoney(data.outstandingDepositTotal)} sub={data.outstandingDepositCount == null ? "Refresh to verify" : `${data.outstandingDepositCount} appointments`} testId="kpi-deposits-due" />
                </div>

                {/* Today's appointments — max 3 */}
                <section className="rounded-2xl glass p-4 md:p-5" data-testid="dash-today-appts">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold">Today</div>
                            <h2 className="font-serif text-2xl text-white mt-1 font-medium">Today's appointments</h2>
                        </div>
                        <Link to="/staff/calendar" className="text-xs text-gold font-semibold hover:underline" data-testid="view-all-today">
                            Open calendar <ArrowRight className="h-3 w-3 inline" />
                        </Link>
                    </div>
                    {data.todaysAppointments.length === 0 ? (
                        <p className="text-sm text-white/70">No appointments today. Enjoy the quiet.</p>
                    ) : (
                        <ul className="divide-y divide-white/10">
                            {(() => {
                                const now = new Date();
                                const parseStart = (a) => {
                                    try { return new Date((a.bookingStart || "").replace(" ", "T")); } catch { return null; }
                                };
                                const withDate = data.todaysAppointments.map(a => ({ a, d: parseStart(a) }));
                                const future = withDate.filter(x => x.d && x.d >= now).sort((a, b) => a.d - b.d);
                                const past   = withDate.filter(x => !x.d || x.d < now).sort((a, b) => (b.d || 0) - (a.d || 0));
                                const list = (future.length ? future : past).slice(0, 3).map(x => x.a);
                                return list.map((a) => <ApptRow key={a.id} appt={a} />);
                            })()}
                        </ul>
                    )}
                </section>

                {/* Recent payments — max 3 */}
                <section className="rounded-2xl glass p-4 md:p-5" data-testid="dash-recent-payments">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold">Recent activity · Past 24 hours</div>
                            <h2 className="font-serif text-2xl text-white mt-1 font-medium">Recent payments</h2>
                        </div>
                        <Link to="/staff/payments" className="text-xs text-gold font-semibold hover:underline" data-testid="view-all-payments">
                            View all <ArrowRight className="h-3 w-3 inline" />
                        </Link>
                    </div>
                    {data.recentActivityAvailable === false ? <p className="text-sm text-white/70">Recent activity could not be loaded. <button onClick={refreshDashboard} className="text-gold underline">Try again</button></p> : recentActivity.length === 0 ? (
                        <p className="text-sm text-white/70">No payments recorded in the past 24 hours.</p>
                    ) : (
                        <ul className="divide-y divide-white/10">
                            {recentActivity.slice(0, 3).map((p) => (
                                <li key={p.id} className="py-2 flex items-center gap-3" data-testid={`recent-payment-${p.id}`}>
                                    <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                                        <CreditCard className="h-4 w-4 text-gold" strokeWidth={1.75} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white font-medium truncate">{p.customerName || "—"}</div>
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-white/60 flex flex-wrap gap-2 mt-0.5">
                                            <span className="truncate">{p.treatment || "—"}</span>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-gold font-ui text-sm font-semibold">{formatMoney(p.amount)}</div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLE[(p.status || "").toLowerCase()] || "bg-white/10 border-white/25 text-white/85"}`}>
                                        {p.status}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
              </>
            )}
        </div>
    );
}

function DashboardSkeleton() {
    return (
        <div className="space-y-4 md:space-y-6" data-testid="dashboard-skeleton">
            <div className="grid grid-cols-3 gap-2.5">
                {[0, 1, 2].map((i) => <SkeletonBlock key={i} className="h-14" />)}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-24" />)}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-24" />)}
            </div>
            <div className="rounded-2xl glass p-4 md:p-5 space-y-3">
                <SkeletonLine className="h-3 w-1/4 bg-white/10" />
                <SkeletonLine className="h-5 w-1/2 bg-white/10" />
                <div className="space-y-2 pt-2">
                    <SkeletonLine className="h-10 w-full bg-white/5" />
                    <SkeletonLine className="h-10 w-full bg-white/5" />
                    <SkeletonLine className="h-10 w-full bg-white/5" />
                </div>
            </div>
        </div>
    );
}

/* ---------- helpers ---------- */
function QuickAction({ to, label, testId }) {
    // Text-only navy pill — no icons, no arrows, equal width via grid parent.
    return (
        <Link to={to} data-testid={testId}
              className="surface-highlight rounded-2xl px-4 py-3 min-h-[68px] w-full flex items-center justify-center text-center hover:brightness-110 transition-all">
            <span className="text-gold text-[13px] md:text-sm font-semibold leading-tight tracking-[0.02em]">
                {label}
            </span>
        </Link>
    );
}

function KPI({ label, value, sub, icon: Icon, onClick, testId }) {
    const clickable = typeof onClick === "function";
    // Single visual variant across the whole dashboard — deep-navy card,
    // white-circle icon, uppercase white label, large gold value.
    return (
        <button
            type="button" onClick={onClick} disabled={!clickable} data-testid={testId}
            className={`surface-highlight rounded-2xl p-4 md:p-5 text-left min-w-0 min-h-[128px] flex flex-col justify-between overflow-hidden ${clickable ? "hover:brightness-110 cursor-pointer" : "cursor-default"}`}
        >
            <div className="flex items-start gap-2.5">
                {Icon && (
                    <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center shrink-0">
                        <Icon className="h-4 w-4 text-white" strokeWidth={1.75} />
                    </div>
                )}
                <div className="text-[10px] md:text-[11px] uppercase tracking-[0.1em] md:tracking-[0.14em] text-white/85 leading-[1.3] pt-1 font-semibold flex-1 min-w-0">
                    {label}
                </div>
            </div>
            <div className="font-serif text-4xl md:text-5xl leading-none whitespace-nowrap text-gold font-medium mt-2">
                {value}
            </div>
            {sub && <div className="text-[11px] md:text-xs text-white/70 truncate">{sub}</div>}
        </button>
    );
}

function ApptRow({ appt }) {
    const time = (appt.bookingStart || "").split("T")[1]?.slice(0, 5) || "—";
    const paid = ((appt.payment || {}).status || "").toLowerCase() === "paid";
    const outstanding = paid ? 0 : Math.max((appt.price || 0) - ((appt.payment || {}).amount || 0), 0);
    return (
        <li className="py-2" data-testid={`appt-row-${appt.id}`}>
            <Link to={`/staff/appointments/${appt.id}`} className="flex items-center gap-3 hover:bg-white/[0.06] rounded-lg px-1 py-1 -mx-1">
                <div className="w-16 font-serif text-xl text-gold font-medium">{time}</div>
                <div className="min-w-0 flex-1">
                    <div className="text-white text-sm font-semibold truncate">
                        {appt.customer?.firstName || "—"} {appt.customer?.lastName || ""}
                    </div>
                    <div className="text-[12px] text-white/70 truncate">{appt.serviceName || "—"}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {(appt.status || "").toLowerCase() === "approved" && (
                        <span title="Approved" className="text-emerald-300 text-[11px]">✓</span>
                    )}
                    {paid && <span title="Deposit paid" className="text-gold text-[11px]">💳</span>}
                    {outstanding > 0 && <span title={`Outstanding ${formatMoney(outstanding)}`} className="text-amber-300 text-[11px]">!</span>}
                </div>
            </Link>
        </li>
    );
}

function greetForNow() {
    try {
        const h = parseInt(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", hour12: false }).format(new Date()), 10);
        if (h < 12) return "Good morning";
        if (h < 18) return "Good afternoon";
        return "Good evening";
    } catch { return "Hello"; }
}

function formatUKDate(iso) {
    if (!iso) return "";
    try { return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }); }
    catch { return iso; }
}

function pluralise(n, one, many) { return `${n} ${n === 1 ? one : many}`; }
